import { useState } from "react";
import { Link } from "react-router-dom";
import { Clock, MapPin, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { InquiryForm } from "./InquiryForm";
import { durationLabel, imgUrl, priceLabel, tourPath } from "../../lib/site";

export const EnquireDialog = ({ tour, open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg" data-testid="enquire-dialog">
      <DialogHeader>
        <DialogTitle className="font-display text-xl font-extrabold">Enquire: {tour?.title || "Plan your trip"}</DialogTitle>
        <DialogDescription>Share your details and our travel expert will call you back.</DialogDescription>
      </DialogHeader>
      <InquiryForm tour={tour} idPrefix="dialog-inquiry" />
    </DialogContent>
  </Dialog>
);

export const TourCard = ({ tour, family }) => {
  const [open, setOpen] = useState(false);
  return (
    <article className="card-lift group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid={`tour-card-${tour.slug}`}>
      <Link to={tourPath(tour)} className="relative block aspect-[4/3] overflow-hidden">
        <img src={imgUrl(tour.cover_image)} alt={`${tour.title} — ${tour.destination}`} loading="lazy"
             className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-[#171717]">
          <Clock className="mr-1 inline h-3.5 w-3.5 text-[#FD691B]" />{durationLabel(tour)}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-6">
        <p className="flex items-center gap-1 text-xs font-medium uppercase tracking-wider text-slate-500"><MapPin className="h-3.5 w-3.5" />{tour.destination}</p>
        <h3 className="mt-2 font-display text-xl font-bold text-[#171717]">{tour.title}</h3>
        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-slate-600">{tour.short_description}</p>
        <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-100 pt-4 text-xs">
          <div><dt className="text-slate-500">{tour.price ? "Starting from" : "Price"}</dt><dd className="mt-0.5 font-display text-sm font-bold text-[#FD691B]" data-testid={`tour-card-price-${tour.slug}`}>{priceLabel(tour)}</dd></div>
          {family ? (
            <div><dt className="text-slate-500">Stay</dt><dd className="mt-0.5 font-semibold">{tour.accommodation_style || "On request"}</dd></div>
          ) : (
            <div><dt className="text-slate-500">Starts from</dt><dd className="mt-0.5 font-semibold">{tour.start_location || "To be confirmed"}</dd></div>
          )}
        </dl>
        {family && tour.activities?.length > 0 && <p className="mt-3 text-xs text-slate-500">Activities: {tour.activities.join(", ")}</p>}
        <div className="mt-auto flex gap-3 pt-6">
          <Link to={tourPath(tour)} className="btn-outline flex-1 !px-4 !py-2.5" data-testid={`tour-card-view-${tour.slug}`}>View Details <ArrowRight className="h-4 w-4" /></Link>
          <button onClick={() => setOpen(true)} className="btn-brand flex-1 !px-4 !py-2.5" data-testid={`tour-card-enquire-${tour.slug}`}>Enquire Now</button>
        </div>
      </div>
      <EnquireDialog tour={tour} open={open} onOpenChange={setOpen} />
    </article>
  );
};
